import { computed, inject, isRef, type Ref, ref, toRef } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { toSentence } from "shared/helpers/array";
import { getMediaForStream } from "shared/helpers/media";
import { isSocialStream } from "shared/helpers/stream";
import StreamFilters from "shared/helpers/StreamFilters";
import { streamFiltersFromFilterSet } from "shared/services/filterSet";
import { useStreamsStore } from "shared/stores/streams";
import type { FilterSet, Nullable, Stream } from "shared/types";

export interface UseStreamFilteringProps {
  filters?: StreamFilters | Ref<StreamFilters>;
  filterSet?: Nullable<FilterSet>;
  stream: Stream;
}

export interface UseStreamFilteringSelected {
  [key: string]: number;
}

export default function useStreamFiltering(options: UseStreamFilteringProps) {
  const stream = toRef<Stream>(options.stream);

  const localFilters = ref<StreamFilters>(
    options.filters && !isRef(options.filters)
      ? options.filters
      : new StreamFilters(stream.value)
  );

  const filters = computed({
    get() {
      return isRef(options.filters)
        ? options.filters.value
        : localFilters.value;
    },
    set(value) {
      if (!isRef(value)) {
        localFilters.value = value || new StreamFilters(stream.value);
      }
    },
  });

  const { getStreamFilters, getStreamFilterSet } = useStreamsStore();

  const streamFilters = getStreamFilters(Number(stream.value.id));

  const filterSet = ref<Nullable<FilterSet>>(null);

  filterSet.value =
    options.filterSet || getStreamFilterSet(Number(stream.value.id));

  if (filterSet.value) {
    filters.value = streamFiltersFromFilterSet(filterSet.value, filters.value);
  } else if (streamFilters) {
    filters.value = streamFilters;
  }

  const selectorActive = ref<Nullable<string>>(null);

  const filtersTotal = computed<number>(() => filters.value.totalFilters());

  const filtering = computed(
    () =>
      Boolean(filtersTotal.value) ||
      filters.value.filteringSortOptions() ||
      Boolean(filterSet.value)
  );

  const $isMobile = inject<boolean>("isMobile");

  const filterLabels = computed(() => {
    const labels = [];

    if ($isMobile) {
      if (filters.value.filteringAuthors()) {
        let label = getLocaleText("stream_filtering.author");

        if (isSocialStream(stream.value)) {
          label += getLocaleText("stream_filtering.handle");
        }

        labels.push(label);
      }

      if (filters.value.filteringContent()) {
        labels.push(getLocaleText("stream_filtering.content"));
      }

      if (filters.value.filteringReactScore()) {
        labels.push(getLocaleText("stream_filtering.react_score"));
      }

      if (filters.value.filteringLocations()) {
        labels.push(getLocaleText("stream_filtering.locations"));
      }

      if (
        filters.value.filteringMedia(getMediaForStream(stream.value as Stream))
      ) {
        labels.push(getLocaleText("stream_filtering.media"));
      }

      if (filters.value.filteringPlatforms()) {
        labels.push(getLocaleText("stream_filtering.social_platforms"));
      }

      if (filters.value.filteringSources()) {
        labels.push(getLocaleText("stream_filtering.sources"));
      }

      if (filters.value.filteringTags()) {
        labels.push(getLocaleText("stream_filtering.tags"));
      }
    } else {
      if (filters.value.filteringKeywords() && !filterSet.value) {
        labels.push(getLocaleText("stream_filtering.keywords"));
      }

      if (filters.value.mediaFiltersCount() && !filterSet.value) {
        labels.push(getLocaleText("stream_filtering.media"));
      }

      if (filters.value.sourcesFiltersCount() && !filterSet.value) {
        labels.push(getLocaleText("stream_filtering.sources"));
      }

      if (filters.value.platformFiltersCount() && !filterSet.value) {
        labels.push(getLocaleText("stream_filtering.social_platforms"));
      }

      if (filters.value.contentFiltersCount()) {
        labels.push(getLocaleText("stream_filtering.content"));
      }

      if (filters.value.dateRangeFiltersCount()) {
        labels.push(getLocaleText("global.date_range"));
      }

      if (filters.value.metricFiltersCount()) {
        labels.push(getLocaleText("stream_filtering.metrics"));
      }

      if (filters.value.otherFiltersCount()) {
        labels.push(getLocaleText("stream_filtering.other"));
      }

      if (filters.value.filteringSortOptions()) {
        labels.push(getLocaleText("stream_filtering.sort_options"));
      }

      if (filterSet.value) {
        labels.push(getLocaleText("global.filter_sets"));
      }
    }

    return labels;
  });

  const filteringLabel = computed(() =>
    getLocaleText("stream_filtering.filtering_by", {
      category: toSentence(filterLabels.value),
    })
  );

  const filtersSelectorActive = computed({
    get() {
      return selectorActive.value === "filters";
    },
    set(value) {
      if (!value) selectorActive.value = null;
      selectorActive.value = "filters";
    },
  });

  const filtersSelected = computed<UseStreamFilteringSelected>(() => ({
    content: filters.value.contentFiltersCount(),
    dateRange: filters.value.dateRangeFiltersCount(),
    keywords: filters.value.keywordsFiltersCount(),
    media: filters.value.mediaFiltersCount(),
    metrics: filters.value.metricFiltersCount(),
    other: filters.value.otherFiltersCount(),
    platforms: filters.value.platformFiltersCount(),
    reports: filters.value.reportsFiltersCount(),
    sources: filters.value.sourcesFiltersCount(),
  }));

  const displayFiltersSelector = () => {
    selectorActive.value = "filters";
  };

  const toggleDisplayFiltersSelector = () => {
    selectorActive.value = filtersSelectorActive.value ? null : "filters";
  };

  return {
    displayFiltersSelector,
    filtering,
    filteringLabel,
    filterLabels,
    filters,
    filterSet,
    filtersSelected,
    filtersSelectorActive,
    filtersTotal,
    selectorActive,
    toggleDisplayFiltersSelector,
  };
}
